import { joinBackward, lift } from 'prosemirror-commands';
import { liftListItem } from 'prosemirror-schema-list';
import { TextSelection } from 'prosemirror-state';
import { replaceStep } from 'prosemirror-transform';
import { isEmpty, isBlank } from '@ember/utils';
import { startOfBlock } from '../helpers/selection';
import { findParentNodeOfType } from 'prosemirror-utils';
export function joinBackwardAddingBreak(state, dispatch, view) {
  return joinBackward(state, function (tr) {
    if (shouldAddBreak(state)) {
      let hardBreak = state.schema.nodes.hardBreak.createAndFill();
      if (hardBreak) {
        tr.insert(state.selection.from - 2, hardBreak);
      }
    }
    if (dispatch) {
      dispatch(tr);
    }
  }, view);
}
export function joinTextBlockToList(state, dispatch) {
  if (!startOfBlock(state)) {
    return false;
  }
  let cutPoint = findCutPoint(state.selection.$anchor);
  if (!cutPoint) {
    return false;
  }
  let nodeBefore = cutPoint.nodeBefore;
  let nodeAfter = cutPoint.nodeAfter;
  if (!nodeBefore || !nodeAfter) {
    return false;
  }
  if (shouldJoinTextBlockToList(state.schema.nodes, nodeBefore, nodeAfter)) {
    let tr = state.tr;
    let endOfLastListItem = findEndOfLastListItem(cutPoint);
    tr.delete(cutPoint.pos, cutPoint.pos + nodeAfter.nodeSize);
    // Take a slice of the textnode after the cut point and stick it onto the end of the last list item in the preceeding list
    let step = replaceStep(state.doc, endOfLastListItem, endOfLastListItem, nodeAfter.slice(0));
    if (step) {
      tr.step(step);
    }
    tr.setSelection(TextSelection.create(tr.doc, endOfLastListItem));
    if (dispatch) {
      dispatch(tr);
    }
    return true;
  }
  return false;
}
function shouldJoinTextBlockToList(nodeTypes, nodeBefore, nodeAfter) {
  let listNodeTypes = [nodeTypes.orderedList, nodeTypes.unorderedList, nodeTypes.orderedNestedList, nodeTypes.unorderedNestedList];
  return !!(nodeAfter.isTextblock && listNodeTypes.includes(nodeBefore.type));
}
function findEndOfLastListItem(cutPoint) {
  let listNode = cutPoint.nodeBefore;
  if (!listNode) {
    return -1;
  }

  // Get to the last list item
  let lastListItemOffset = 1; // Start by entering the outer </ul> or </ol> node
  let lastItem = listNode.content.lastChild;
  let nodeTypesToWalk = ['listItem', 'nestedListItem', 'unorderedNestedList', 'orderedNestedList'];
  while (lastItem && nodeTypesToWalk.includes(lastItem?.type.name)) {
    lastListItemOffset += 1; // Enter the </li> or </ul> or </ol> node
    if (!lastItem.content.lastChild) {
      break;
    } else {
      lastItem = lastItem.content.lastChild;
    }
  }

  // Enter the paragraph node (i.e. cross </p>) if list item ends with a paragraph
  if (lastItem?.type.name === 'paragraph') {
    lastListItemOffset += 1;
  }
  return cutPoint.pos - lastListItemOffset;
}
export function liftIfStartOfListItem(state, dispatch) {
  if (!startOfBlock(state)) {
    return false;
  }
  let $anchor = state.selection.$anchor;
  let positionBefore = $anchor.doc.resolve($anchor.before());
  let parentType = positionBefore.parent.type;
  if (parentType === state.schema.nodes.listItem) {
    return lift(state, dispatch);
  }
  let parentOffset = positionBefore.parentOffset;
  let isFirstListItem = positionBefore.parent === positionBefore.node(-1)?.firstChild;
  if (parentType === state.schema.nodes.nestedListItem && parentOffset === 0 && isFirstListItem) {
    return liftListItem(parentType)(state, dispatch);
  }
  return false;
}
export function liftIfStartOfCallout(state, dispatch) {
  if (!startOfBlock(state)) {
    return false;
  }
  let $anchor = state.selection.$anchor;
  let positionBefore = $anchor.doc.resolve($anchor.before());
  let parent = positionBefore.parent;
  let tr = state.tr;
  let calloutElement = findParentNodeOfType(state.schema.nodes.callout)(state.selection);
  if (!calloutElement) {
    return false;
  }
  let calloutStart = calloutElement.pos;
  let calloutEnd = calloutElement.pos + calloutElement.node.nodeSize;
  let newContent = parent.content;
  let isFirstNodeInCallout = isBlank(positionBefore.nodeBefore);
  if (dispatch && newContent && isFirstNodeInCallout) {
    tr = tr.replaceWith(calloutStart, calloutEnd, newContent);
    let textSelection = TextSelection.create(tr.doc, calloutStart + 1);
    tr.setSelection(textSelection).scrollIntoView();
    dispatch(tr);
    return true;
  }
  return false;
}
export function deleteEmptyParagraph(state, dispatch) {
  if (!startOfBlock(state)) {
    return false;
  }
  let $anchor = state.selection.$anchor;
  let paragraph = state.selection.$from.parent;
  let positionBefore = $anchor.doc.resolve($anchor.before());
  let parent = positionBefore.parent;
  let parentIsContainer = parent.type === state.schema.nodes.tableCell && parent.attrs.container;
  let tableCellHasOneEmptyParagraph = parent.content.childCount === 1 && isEmpty(paragraph.content);
  if (parentIsContainer && tableCellHasOneEmptyParagraph) {
    let tr = state.tr;
    tr.delete(state.selection.$from.pos - 1, state.selection.$to.pos + 1);
    if (dispatch) {
      dispatch(tr);
    }
  }
  return false;
}
function shouldAddBreak(state) {
  let cutPoint = findCutPoint(state.selection.$anchor);
  let nodeBefore = cutPoint?.nodeBefore;
  let nodeAfter = cutPoint?.nodeAfter;
  let nodeTypes = state.schema.nodes;
  let nodeTypesWithBreaks = [nodeTypes.codeBlock];
  let nodeTypesThatLift = [nodeTypes.orderedList, nodeTypes.unorderedList];
  return nodeBefore && nodeTypesWithBreaks.includes(nodeBefore.type) && nodeAfter && !nodeTypesThatLift.includes(nodeAfter.type);
}
function findCutPoint($pos) {
  if (!$pos.parent.type.spec.isolating) {
    for (let i = $pos.depth - 1; i >= 0; i--) {
      if ($pos.index(i) > 0) {
        return $pos.doc.resolve($pos.before(i + 1));
      }
      if ($pos.node(i).type.spec.isolating) {
        break;
      }
    }
  }
  return null;
}